var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { z } from 'zod';
import { createJsonCommandExtractor, jsonParser } from './framework/JsonCommandExtractor';
const debug = false;
const allowedMuteTypes = ['input', 'output'];
const allowedVolumeTypes = ['input', 'output'];
const allowedTypes = {
    amplifier: allowedVolumeTypes,
    assistiveListeningMute: allowedMuteTypes,
    assistiveListeningMuteStatus: allowedMuteTypes,
    assistiveListeningVolume: allowedVolumeTypes,
    audioInputMute: allowedMuteTypes,
    audioInputMuteStatus: allowedMuteTypes,
    audioInputVolume: allowedVolumeTypes,
    mics: allowedMuteTypes,
    micsMuteStatus: allowedMuteTypes,
    overflowRoomMute: allowedMuteTypes,
    overflowRoomMuteStatus: allowedMuteTypes,
    overflowRoomVolume: allowedVolumeTypes,
    preset: [],
    receiveGain: [],
    soundReinforcementMute: allowedMuteTypes,
    soundReinforcementMuteStatus: allowedMuteTypes,
    soundReinforcementVolume: allowedVolumeTypes,
    telephony: [],
};
function isNetvioAudioChannelConfig(config) {
    return typeof config === 'string';
}
// Zod definitions
const netvioResponse = z.object({
    id: z.union([z.string(), z.null()]).optional(),
    jsonrpc: z.string(),
    error: z
        .object({
        code: z.number(),
        message: z.string().optional(),
        data: z.string().optional(),
    })
        .optional(),
    result: z
        .object({
        cmdStatus: z.string().optional(),
        signalNode: z.string().optional(),
        assignSignal: z.string().optional(),
        type: z.string().optional(),
        sessionID: z.number().optional(),
        username: z.string().optional(),
        fwVersion: z.string().optional(),
    })
        .optional(),
});
const netvioMatrixNotifyResponse = z.object({
    id: z.union([z.string(), z.null()]).optional(),
    jsonrpc: z.string(),
    method: z.string(),
    params: z
        .object({
        endpointID: z.string(),
        signals: z.array(z.object({
            signalNode: z.string(),
            assignSignal: z.union([z.string(), z.array(z.string()), z.null()]).optional(),
        })),
    })
        .optional(),
});
const netvioVolumeQueryResponse = z.object({
    jsonrpc: z.string(),
    result: z.object({
        endpointID: z.string(),
        processID: z.string().optional(),
        audioOutVolume: z.number().optional(),
        audioOutMute: z.boolean().optional(),
        audioOutDelay: z.number().optional(),
    }),
    id: z.union([z.string(), z.null()]).optional(),
});
const netvioMatrixQueryResponse = z.object({
    jsonrpc: z.string(),
    result: z.union([
        z.object({
            endpointID: z.string().optional(),
            signalNode: z.string(),
            assignSignal: z.union([z.string(), z.array(z.string()), z.null()]).optional(),
        }),
        z.object({
            endpointID: z.string().optional(),
            signals: z.array(z.object({
                signalNode: z.string(),
                assignSignal: z.union([z.string(), z.array(z.string()), z.null()]).optional(),
            })),
            cmdStatus: z.string().optional(),
        }),
    ]),
    id: z.union([z.string(), z.null()]).optional(),
});
const getDevicesResponse = z.object({
    jsonrpc: z.string(),
    id: z.union([z.string(), z.null()]).optional(),
    result: z.array(z.object({
        alias: z.string().optional(),
        endpointID: z.string(),
        endpointType: z.string(),
        fileName: z.string().optional(),
        hostname: z.string(),
        index: z.number(),
        signals: z.array(z
            .object({
            assignSignal: z
                .union([z.string(), z.array(z.string()), z.null()])
                .optional(),
            availPorts: z.array(z.string()),
            direction: z.string().optional(),
            dstPort: z.string().optional(),
            inAudioChannel: z.number().optional(),
            inAudioFreq: z.number().optional(),
            inAudioSample: z.number().optional(),
            inAudioType: z.string().optional(),
            inVideoFPS: z.number().optional(),
            inVideoResolution: z.string().optional(),
            map: z.number().optional(),
            signalNode: z.string(),
            srcPort: z.string().optional(),
            type: z.string().optional(),
            usbEnumKBM: z.boolean().optional(),
        })
            .optional()),
        processing: z
            .array(z
            .object({
            //acsConfig: z.string().optional(),
            audioOutDelay: z.number().optional(),
            audioOutMute: z.boolean().optional(),
            audioOutVolume: z.number().optional(),
            // other fields we don't use
            processID: z.number().optional(),
            processName: z.string().optional(),
            videoEnable: z.boolean().optional(),
            videoMute: z.boolean().optional(),
            videoRotation: z.number().optional(),
            viewPortPixel: z.string().optional(),
            viewPortPM: z.string().optional(),
        })
            .optional())
            .optional(),
        ddc: z
            .array(z
            .object({
            ddcPort: z.string().optional(),
            hdcpMode: z.string().optional(),
            edidData: z.string().optional(),
            linkStatus: z.boolean().optional(),
        })
            .optional())
            .optional(),
        uart: z
            .array(z
            .object({
            uartBaud: z.number().optional(),
            uartBypass: z.boolean().optional(),
            uartByte: z.number().optional(),
            uartDelim: z.string().optional(),
            uartID: z.number().optional(),
            uartParity: z.string().optional(),
            uartPort: z.string().optional(),
            uartRxFormat: z.string().optional(),
            uartStop: z.number().optional(),
        })
            .optional())
            .optional(),
        interfaces: z
            .array(z
            .object({
            autoDNS: z.boolean().optional(),
            dns1: z.string().optional(),
            dns2: z.string().optional(),
            gateway: z.string().optional(),
            hostIP: z.string().optional(),
            interfaceID: z.number().optional(),
            interfaceMetric: z.number().optional(),
            mode: z.string().optional(),
            netmask: z.string().optional(),
            VID: z.number().optional(),
            presetVLANID: z.boolean().optional(),
        })
            .optional())
            .optional(),
        workingMode: z.string().optional(),
        transport: z.string().optional(),
        identifyStatus: z.string().optional(),
        powerStatus: z.string().optional(),
        onlineStatus: z.string().optional(),
        fwVersion: z.string().optional(),
    })),
});
function isResp(schema, resp) {
    return schema.passthrough().safeParse(resp).success;
}
export class Netvio {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.endpoints = {}; // details on all inputs/outputs
        this.cmdId = 0;
        this.routeAudioWithVideo = true; // will come from a config in a future version
        this.routeAllSignals = false; // route video, audio, serial, usb with switching commands
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.getDetectedFromEndpoints();
            }),
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                const getFwVersion = {
                    jsonrpc: '2.0',
                    method: 'get_device_fw',
                };
                const resp = yield this.sendCommand(getFwVersion, netvioResponse, tags);
                return (_b = (_a = resp === null || resp === void 0 ? void 0 : resp.result) === null || _a === void 0 ? void 0 : _a.fwVersion) !== null && _b !== void 0 ? _b : 'unknown';
            }),
        };
        this.mute = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _c, _d;
                const getAudioMute = {
                    jsonrpc: '2.0',
                    method: 'get_audio_volume',
                    params: {
                        target: key,
                    },
                };
                const resp = yield this.sendCommand(getAudioMute, netvioVolumeQueryResponse, tags);
                const muteStatus = (_d = (_c = resp === null || resp === void 0 ? void 0 : resp.result) === null || _c === void 0 ? void 0 : _c.audioOutMute) !== null && _d !== void 0 ? _d : this.state.mutes.values[key];
                return muteStatus;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _e;
                const id = this.makeId();
                const setAudioMute = {
                    jsonrpc: '2.0',
                    method: 'set_audio_volume',
                    params: {
                        target: key,
                        audioOutMute: value,
                    },
                    id,
                };
                const resp = yield this.sendCommand(setAudioMute, netvioResponse, tags);
                if (((_e = resp === null || resp === void 0 ? void 0 : resp.result) === null || _e === void 0 ? void 0 : _e.cmdStatus) === 'success') {
                    return value;
                }
                return this.state.mutes.values[key];
            }),
            getKey: this.getKey.bind(this),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                let getMatrixRoute;
                if (this.routeAllSignals) {
                    getMatrixRoute = {
                        jsonrpc: '2.0',
                        method: 'get_matrix_assign',
                        params: {
                            target: key,
                        },
                    };
                }
                else {
                    getMatrixRoute = {
                        jsonrpc: '2.0',
                        method: 'get_matrix_assign',
                        params: {
                            signalNode: key,
                        },
                    };
                }
                const resp = yield this.sendCommand(getMatrixRoute, netvioMatrixQueryResponse, tags);
                if ((resp === null || resp === void 0 ? void 0 : resp.result) && 'signalNode' in (resp === null || resp === void 0 ? void 0 : resp.result) && resp.result['signalNode'] === key) {
                    if ('assignSignal' in resp.result) {
                        const assignSignal = resp.result['assignSignal'];
                        if (assignSignal && !Array.isArray(assignSignal)) {
                            return assignSignal;
                        }
                    }
                }
                else if (
                // response from getting the entire endpoint (routeAllSignals === true)
                (resp === null || resp === void 0 ? void 0 : resp.result) &&
                    'signals' in (resp === null || resp === void 0 ? void 0 : resp.result) &&
                    'endpointID' in (resp === null || resp === void 0 ? void 0 : resp.result) &&
                    (resp === null || resp === void 0 ? void 0 : resp.result['endpointID']) === key) {
                    const signals = resp === null || resp === void 0 ? void 0 : resp.result.signals;
                    for (const signal of signals !== null && signals !== void 0 ? signals : []) {
                        if (signal.signalNode.includes('video')) {
                            const endpointId = this.getEndpointIdFromSignal(signal.assignSignal);
                            if (endpointId)
                                return endpointId;
                            break;
                        }
                    }
                }
                return this.state.videoMatrixRoutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _f, _g;
                let videoMatrixRoute;
                if (this.routeAllSignals) {
                    videoMatrixRoute = {
                        jsonrpc: '2.0',
                        method: 'set_matrix_assign',
                        params: {
                            target: key,
                            assign: value,
                        },
                    };
                }
                else {
                    // assign the video input to video output
                    videoMatrixRoute = {
                        jsonrpc: '2.0',
                        method: 'set_matrix_assign',
                        params: {
                            signalNode: key,
                            assignSignal: value,
                        },
                    };
                }
                const resp = yield this.sendCommand(videoMatrixRoute, netvioResponse, tags);
                if (!this.routeAllSignals && this.routeAudioWithVideo) {
                    // translate the keys to use the audio channels
                    const audioKey = key.replace('video', 'audio');
                    const audioValue = value.replace('video', 'audio');
                    const audioMatrixRoute = {
                        jsonrpc: '2.0',
                        method: 'set_matrix_assign',
                        params: {
                            signalNode: audioKey,
                            assignSignal: audioValue,
                        },
                    };
                    yield this.sendCommand(audioMatrixRoute, netvioResponse, tags);
                }
                if (((_g = (_f = resp === null || resp === void 0 ? void 0 : resp.result) === null || _f === void 0 ? void 0 : _f.cmdStatus) !== null && _g !== void 0 ? _g : '').toLowerCase() === 'success') {
                    return value;
                }
                return this.state.videoMatrixRoutes.values[key];
            }),
            asyncHandlers: {
                match: resp => (isResp(netvioMatrixNotifyResponse, resp) ? resp : undefined),
                callback: (resp) => {
                    this.processMatrixRouting(resp, 'video');
                },
            },
        };
        this.volume = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                var _h, _j;
                const getAudioVolume = {
                    jsonrpc: '2.0',
                    method: 'get_audio_volume',
                    params: {
                        target: key,
                    },
                };
                const resp = yield this.sendCommand(getAudioVolume, netvioVolumeQueryResponse, tags);
                const volumeStatus = (_j = (_h = resp === null || resp === void 0 ? void 0 : resp.result) === null || _h === void 0 ? void 0 : _h.audioOutVolume) !== null && _j !== void 0 ? _j : this.state.volumes.values[key];
                return volumeStatus;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _k;
                const setAudioVolume = {
                    jsonrpc: '2.0',
                    method: 'set_audio_volume',
                    params: {
                        target: key,
                        audioOutVolume: value,
                    },
                };
                const resp = yield this.sendCommand(setAudioVolume, netvioResponse, tags);
                if (((_k = resp === null || resp === void 0 ? void 0 : resp.result) === null || _k === void 0 ? void 0 : _k.cmdStatus) === 'success') {
                    return value;
                }
                return this.state.volumes.values[key];
            }),
            getKey: this.getKey.bind(this),
            range: (config, info) => __awaiter(this, void 0, void 0, function* () {
                if (info.volumeType === 'trim')
                    return { max: 100, min: 50, round: 1 };
                return { max: 100, min: 30, round: 1 };
            }),
            validate: this.validate.bind(this), // happens before constructor binding
        };
        // polling
        this.polls = [
            {
                intervalMs: 5000,
                controls: ['videoMatrixRoutes'],
            },
            {
                intervalMs: 15 * 60 * 1000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.checkForNewEndpoints(['checkForNewEndpoints', 'poll']);
                }),
            },
            {
                intervalMs: 10000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.pingNetvio(['poll']);
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        return __awaiter(this, void 0, void 0, function* () {
            this.endpoints = {};
            yield this.checkForNewEndpoints(['configure']);
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            const disconnect = {
                jsonrpc: '2.0',
                method: 'set_session_logout',
            };
            yield this.sendCommand(disconnect, netvioResponse, ['disconnect']);
        });
    }
    onInitialize(credentials) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            const connectCmd = {
                jsonrpc: '2.0',
                method: 'set_session_login',
                params: {
                    username: (_a = credentials === null || credentials === void 0 ? void 0 : credentials.username) !== null && _a !== void 0 ? _a : 'admin',
                    pw: (_b = credentials === null || credentials === void 0 ? void 0 : credentials.password) !== null && _b !== void 0 ? _b : 'password',
                },
            };
            const resp = yield this.sendCommand(connectCmd, netvioResponse, ['initialize']);
            if (((_d = (_c = resp === null || resp === void 0 ? void 0 : resp.result) === null || _c === void 0 ? void 0 : _c.cmdStatus) !== null && _d !== void 0 ? _d : '').toLowerCase() === 'success') {
                return 'connected';
            }
            else if (((_f = (_e = resp === null || resp === void 0 ? void 0 : resp.error) === null || _e === void 0 ? void 0 : _e.data) !== null && _f !== void 0 ? _f : '').toLowerCase().includes('incorrect username/password')) {
                return 'authFailed';
            }
            return 'verifyFailed';
        });
    }
    // other class methods
    checkForNewEndpoints(tags, updateDetected = false) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            let newEndpointsFound = false;
            const getEndPointCmd = {
                jsonrpc: '2.0',
                method: 'get_endpoint',
            };
            const resp = yield this.sendCommand(getEndPointCmd, getDevicesResponse, tags, { sendTimeoutMs: 10000 } // for larger systems
            );
            const endpointInfo = (_a = resp === null || resp === void 0 ? void 0 : resp.result) !== null && _a !== void 0 ? _a : [];
            for (const ep of endpointInfo) {
                if (ep.endpointID.startsWith('ctl') || // ignore the control device
                    this.endpoints[ep.endpointID] !== undefined // ignore already known endpoints
                ) {
                    continue;
                }
                newEndpointsFound = true;
                this.log.info(`found new endpoint: ${ep.endpointID}`);
                const signals = (_b = ep === null || ep === void 0 ? void 0 : ep.signals) !== null && _b !== void 0 ? _b : [];
                const endpointSignalInfo = [];
                for (const signal of signals) {
                    endpointSignalInfo.push({
                        type: ((_c = signal === null || signal === void 0 ? void 0 : signal.type) !== null && _c !== void 0 ? _c : 'video'),
                        assignSignal: (signal === null || signal === void 0 ? void 0 : signal.assignSignal) !== null ? signal === null || signal === void 0 ? void 0 : signal.assignSignal : undefined,
                        signalNode: signal === null || signal === void 0 ? void 0 : signal.signalNode,
                    });
                }
                this.endpoints[ep.endpointID] = {
                    hostname: ep.hostname,
                    alias: ep === null || ep === void 0 ? void 0 : ep.alias,
                    endpointId: ep.endpointID,
                    endpointType: ep.endpointID.startsWith('in') ? 'encoder' : 'decoder',
                    signals: endpointSignalInfo,
                };
            }
            if (newEndpointsFound && updateDetected) {
                this.state.detected.update(this.getDetectedFromEndpoints());
            }
        });
    }
    computeFilters(type) {
        return {
            amplifier: allowedTypes.amplifier.includes(type),
            assistiveListeningMute: allowedTypes.assistiveListeningMute.includes(type),
            assistiveListeningMuteStatus: allowedTypes.assistiveListeningMuteStatus.includes(type),
            assistiveListeningVolume: allowedTypes.assistiveListeningVolume.includes(type),
            audioInputMute: allowedTypes.audioInputMute.includes(type),
            audioInputMuteStatus: allowedTypes.audioInputMuteStatus.includes(type),
            audioInputVolume: allowedTypes.audioInputVolume.includes(type),
            mics: allowedTypes.mics.includes(type),
            micsMuteStatus: allowedTypes.micsMuteStatus.includes(type),
            overflowRoomMute: allowedTypes.overflowRoomMute.includes(type),
            overflowRoomMuteStatus: allowedTypes.overflowRoomMuteStatus.includes(type),
            overflowRoomVolume: allowedTypes.overflowRoomVolume.includes(type),
            preset: false,
            receiveGain: false,
            soundReinforcementMute: allowedTypes.soundReinforcementMute.includes(type),
            soundReinforcementMuteStatus: allowedTypes.soundReinforcementMuteStatus.includes(type),
            soundReinforcementVolume: allowedTypes.soundReinforcementVolume.includes(type),
            telephony: false,
        };
    }
    // Returns the name of the channel with the given signalType from the list of signals associated
    // with the endpoint.
    findSignalName(endpoint, signalType) {
        var _a;
        const signal = ((_a = endpoint === null || endpoint === void 0 ? void 0 : endpoint.signals) !== null && _a !== void 0 ? _a : []).find(sig => sig.type === signalType);
        return signal === null || signal === void 0 ? void 0 : signal.signalNode;
    }
    getDetectedFromEndpoints() {
        var _a, _b;
        const videoInputs = [];
        const videoOutputs = [];
        const channels = [];
        // Loop through the locally stored endpoint information to create detected channels. If
        // routing all signals, then use the endpoint IDs for the channels, such as "out1",
        // otherwise use the individual signal names, such as "out1-videorx1".
        for (const [id, endpoint] of Object.entries(this.endpoints)) {
            const name = (endpoint === null || endpoint === void 0 ? void 0 : endpoint.alias) && (endpoint === null || endpoint === void 0 ? void 0 : endpoint.alias.length) > 0 ? endpoint === null || endpoint === void 0 ? void 0 : endpoint.alias : endpoint.hostname;
            if ((endpoint === null || endpoint === void 0 ? void 0 : endpoint.endpointType) === 'encoder') {
                videoInputs.push({
                    code: this.routeAllSignals
                        ? id
                        : (_a = this.findSignalName(endpoint, 'video')) !== null && _a !== void 0 ? _a : 'video',
                    external: true,
                    name,
                });
            }
            else {
                videoOutputs.push({
                    code: this.routeAllSignals
                        ? id
                        : (_b = this.findSignalName(endpoint, 'video')) !== null && _b !== void 0 ? _b : 'video',
                    name,
                });
                // Audio outputs can be controlled, audio inputs can't.
                channels.push({
                    channel: id,
                    type: 'output',
                    name,
                    filters: this.computeFilters('output'),
                });
            }
        }
        if (debug) {
            this.log.debug(`getDetected audio channels = ${JSON.stringify(channels, null, 4)}`);
            this.log.debug(`getDetected videoInputs = ${JSON.stringify(videoInputs, null, 4)}`);
            this.log.debug(`getDetected videoOutputs = ${JSON.stringify(videoOutputs, null, 4)}`);
        }
        // Inara/kaylee doesn't support drivers with both detected audio DSP channels and detected
        // video inputs and outputs.
        return { videoInputs, videoOutputs }; //, channels };
    }
    getEndpointIdFromSignal(signalName) {
        const name = Array.isArray(signalName) ? signalName[0] : signalName;
        if (name)
            return name.replace(/((?:out|in)\d+)-.+/, '$1');
        return undefined;
    }
    getKey(config) {
        if (isNetvioAudioChannelConfig(config))
            return config;
        throw new Error(`invalid config: ${JSON.stringify(config)}`);
    }
    makeId() {
        if (this.cmdId++ > 999)
            this.cmdId = 0;
        return `aveo${this.cmdId.toString().padStart(3, '0')}`;
    }
    // Query session login to to keep the TCP network connection alive.
    pingNetvio(tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const pingCmd = {
                jsonrpc: '2.0',
                method: 'get_session_login',
            };
            yield this.sendCommand(pingCmd, netvioResponse, tags);
        });
    }
    processMatrixRouting(info, signalType) {
        var _a, _b, _c, _d, _e, _f;
        // process async responses about video outputs
        const signalRegEx = new RegExp(`(out\\d+\\-${signalType}.+)`, 'i');
        const signals = (_b = (_a = info === null || info === void 0 ? void 0 : info.params) === null || _a === void 0 ? void 0 : _a.signals) !== null && _b !== void 0 ? _b : [];
        if (debug)
            this.log.debug(`processMatrixRouting = ${JSON.stringify(info, null, 4)}`);
        for (const signal of signals) {
            if (((_d = (_c = info === null || info === void 0 ? void 0 : info.params) === null || _c === void 0 ? void 0 : _c.endpointID) !== null && _d !== void 0 ? _d : '').includes('out')) {
                // Ignore async updates about inputs because we don't need it.
                const videoInput = (_e = signal === null || signal === void 0 ? void 0 : signal.assignSignal) !== null && _e !== void 0 ? _e : '';
                const videoOutput = (_f = signal === null || signal === void 0 ? void 0 : signal.signalNode) !== null && _f !== void 0 ? _f : '';
                const match = signalRegEx.exec(videoOutput);
                if (match && match[1]) {
                    if (debug) {
                        this.log.debug(`updating route of input: ${videoInput} to output ${videoOutput}`);
                    }
                    if (videoInput && !Array.isArray(videoInput)) {
                        if (this.routeAllSignals) {
                            const outId = this.getEndpointIdFromSignal(videoOutput);
                            const inId = this.getEndpointIdFromSignal(videoInput);
                            if (outId && inId) {
                                this.state.videoMatrixRoutes.update(outId, inId);
                            }
                        }
                        else {
                            this.state.videoMatrixRoutes.update(videoOutput, videoInput);
                        }
                        break;
                    }
                }
            }
        }
    }
    sendCommand(cmd, schema, tags, options) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const id = this.makeId();
            cmd.id = id;
            const resp = yield this.socket.send(JSON.stringify(cmd), resp => typeof resp === 'object' &&
                resp !== null &&
                'id' in resp &&
                (resp.id === id || resp.id === null) // a failed login response returns null for id
                ? resp
                : undefined, tags !== null && tags !== void 0 ? tags : [], options);
            const parsedResponse = resp !== undefined && isResp(schema, resp) ? resp : undefined;
            if (parsedResponse === undefined && !((_a = options === null || options === void 0 ? void 0 : options.allowError) !== null && _a !== void 0 ? _a : false)) {
                this.log.error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return parsedResponse;
        });
    }
    validate(config, info) {
        return __awaiter(this, void 0, void 0, function* () {
            if (debug) {
                const key = this.getKey(config);
                const configStr = JSON.stringify(config);
                const infoStr = JSON.stringify(info);
                this.log.debug(`validate: key = ${key}, config = ${configStr}, info: ${infoStr}`);
            }
            if (isNetvioAudioChannelConfig(config)) {
                if (debug)
                    this.log.debug(`validChannel: ${JSON.stringify(config, null, 4)}`);
                return 'ok';
            }
            this.log.error(`invalid config: ${JSON.stringify(config)}`);
            return 'incompatible';
        });
    }
}
// static properties
// Inara/kaylee doesn't support drivers with both detected audio DSP channels and detected video
// inputs and outputs.
// public static readonly channelsConfigName = 'netvioControlChannels';
Netvio.driverType = 'socketDriver';
Netvio.key = '-O46FUaHKiQKzDCezCz0';
Netvio.socketOptions = [
    {
        commandExtractor: createJsonCommandExtractor({ nonJsonBehavior: 'extract' }),
        parser: command => jsonParser(command),
        port: 23,
        sendTerminator: '\r\n',
        idleTimeoutMs: 25000,
    },
];
